import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { FileSignature, ArrowRight, Loader2, Search, CheckCircle2, BellRing } from "lucide-react";
import { toast } from "sonner";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const CATEGORY_LABELS = {
    busta_paga: "Busta paga",
    cu_730: "CU / 730",
    contratto: "Contratto",
    certificato: "Certificato formazione",
    comunicazione: "Comunicazione",
    visita_medica: "Visita medica",
    documento_identita: "Documento identità",
    unilav: "Ricevuta UNILAV",
    atti: "Atti",
    altro: "Altro",
};

const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return iso; }
};

const daysSince = (iso) => {
    if (!iso) return null;
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    return diff < 0 ? 0 : diff;
};

export default function PendingSignaturesPage() {
    const [data, setData] = useState({ total: 0, by_category: {}, items: [] });
    const [category, setCategory] = useState("");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [remindingId, setRemindingId] = useState(null);
    const [remindingAll, setRemindingAll] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        let mounted = true;
        const t = setTimeout(() => {
            setLoading(true);
            api.get("/documents/pending-ack", {
                params: { limit: 2000, category: category || undefined, search: search || undefined },
            })
                .then((r) => mounted && setData(r.data))
                .catch(() => {})
                .finally(() => mounted && setLoading(false));
        }, search ? 350 : 0);
        return () => { mounted = false; clearTimeout(t); };
    }, [category, search, refreshKey]);

    const handleRemind = async (it) => {
        setRemindingId(it.employee_id);
        try {
            const { data: res } = await api.post("/documents/pending-ack/remind", { employee_id: it.employee_id });
            if (res.sent) {
                toast.success(`Sollecito inviato a ${it.employee_label} (${res.documents} documenti)`);
            } else {
                toast.warning(`Sollecito registrato per ${it.employee_label} — invio email disattivato fino al go-live`);
            }
            setRefreshKey((k) => k + 1);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore nell'invio del sollecito");
        } finally {
            setRemindingId(null);
        }
    };

    const handleRemindAll = async () => {
        setRemindingAll(true);
        try {
            const { data: res } = await api.post("/documents/pending-ack/remind-all");
            const skipped = res.skipped_no_email > 0 ? ` — ${res.skipped_no_email} senza email saltati` : "";
            if (res.emails_sent > 0) {
                toast.success(`Sollecito inviato a ${res.employees_reminded} dipendenti (${res.documents} documenti)${skipped}`);
            } else {
                toast.warning(`Sollecito registrato per ${res.employees_reminded} dipendenti (${res.documents} documenti)${skipped} — invio email disattivato fino al go-live`);
            }
            setRefreshKey((k) => k + 1);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore nel sollecito massivo");
        } finally {
            setRemindingAll(false);
        }
    };

    const cats = Object.entries(data.by_category || {}).sort((a, b) => b[1] - a[1]);
    const grandTotal = cats.reduce((s, [, n]) => s + n, 0);

    return (
        <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8" data-testid="pending-signatures-page">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="flex items-center gap-2 font-display text-2xl font-bold text-[#376DB3]">
                        <FileSignature className="h-6 w-6" />
                        Documenti da firmare
                    </h1>
                    <p className="mt-1 text-sm text-slate-600">
                        Documenti in attesa di presa visione da parte dei dipendenti (escluse le buste paga).
                    </p>
                </div>
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <button
                            type="button"
                            disabled={remindingAll || grandTotal === 0}
                            className="inline-flex items-center gap-2 rounded-md bg-[#376DB3] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#376DB3]/90 disabled:opacity-50"
                            data-testid="remind-all-btn"
                        >
                            {remindingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />}
                            Sollecita tutti
                        </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="remind-all-confirm">
                        <AlertDialogHeader>
                            <AlertDialogTitle>Sollecitare tutti i dipendenti?</AlertDialogTitle>
                            <AlertDialogDescription>
                                Verrà preparata una email di sollecito per ogni dipendente con documenti in attesa di presa visione ({grandTotal} documenti totali). Con gli invii email attualmente disattivati, il sollecito verrà solo registrato.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel data-testid="remind-all-cancel">Annulla</AlertDialogCancel>
                            <AlertDialogAction onClick={handleRemindAll} className="bg-[#376DB3] hover:bg-[#376DB3]/90" data-testid="remind-all-confirm-btn">
                                Sollecita tutti
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>

            {/* Filters */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    onClick={() => setCategory("")}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${!category ? "border-[#376DB3] bg-[#376DB3] text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
                    data-testid="filter-cat-all"
                >
                    Tutte ({grandTotal})
                </button>
                {cats.map(([cat, count]) => (
                    <button
                        key={cat}
                        type="button"
                        onClick={() => setCategory(category === cat ? "" : cat)}
                        className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${category === cat ? "border-[#376DB3] bg-[#376DB3] text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
                        data-testid={`filter-cat-${cat}`}
                    >
                        {CATEGORY_LABELS[cat] || cat} ({count})
                    </button>
                ))}
                <div className="relative ml-auto w-full sm:w-72">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Cerca dipendente, matricola o file…"
                        className="pl-9"
                        data-testid="pending-sign-search"
                    />
                </div>
            </div>

            <div className="mb-3 text-sm text-slate-500" data-testid="pending-sign-count">
                <b className="text-slate-700">{data.total}</b> documenti in attesa
            </div>

            {loading ? (
                <div className="flex h-48 items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
            ) : data.total === 0 ? (
                <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white py-14 text-slate-500" data-testid="pending-sign-empty">
                    <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                    <p className="text-sm">Nessun documento in attesa di presa visione.</p>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                    <table className="w-full min-w-[640px] text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                                <th className="px-4 py-2.5">Dipendente</th>
                                <th className="px-4 py-2.5">Categoria</th>
                                <th className="hidden px-4 py-2.5 md:table-cell">Documento</th>
                                <th className="px-4 py-2.5">Assegnato il</th>
                                <th className="px-4 py-2.5">Attesa</th>
                                <th className="px-2 py-2.5"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map((it) => {
                                const days = daysSince(it.uploaded_at);
                                return (
                                    <tr key={it.document_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`pending-sign-row-${it.document_id}`}>
                                        <td className="px-4 py-2.5">
                                            <Link to={`/dipendenti/${it.employee_id}`} className="font-medium text-slate-700 hover:text-[#376DB3]">
                                                {it.employee_label}
                                            </Link>
                                            {it.matricola && <span className="ml-1.5 text-xs text-slate-400">#{it.matricola}</span>}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <span className="rounded-full bg-[#376DB3]/10 px-2 py-0.5 text-[11px] font-semibold text-[#376DB3]">
                                                {it.category_label}
                                            </span>
                                        </td>
                                        <td className="hidden max-w-[280px] truncate px-4 py-2.5 text-slate-600 md:table-cell" title={it.title || it.filename}>
                                            {it.title || it.filename}
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">{fmtDate(it.uploaded_at)}</td>
                                        <td className="whitespace-nowrap px-4 py-2.5">
                                            <span className={days >= 7 ? "font-semibold text-amber-700" : "text-slate-600"}>
                                                {days === 0 ? "oggi" : `${days} gg`}
                                            </span>
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-2.5">
                                            <button
                                                type="button"
                                                onClick={() => handleRemind(it)}
                                                disabled={remindingId === it.employee_id}
                                                title="Invia sollecito di presa visione al dipendente"
                                                className="inline-flex items-center gap-1.5 rounded-md border border-[#376DB3]/30 bg-[#376DB3]/5 px-2.5 py-1 text-[11px] font-semibold text-[#376DB3] transition-colors hover:bg-[#376DB3]/10 disabled:opacity-50"
                                                data-testid={`remind-btn-${it.document_id}`}
                                            >
                                                {remindingId === it.employee_id
                                                    ? <Loader2 className="h-3 w-3 animate-spin" />
                                                    : <BellRing className="h-3 w-3" />}
                                                Sollecita
                                            </button>
                                            {it.ack_reminder_sent_at && (
                                                <div className="mt-0.5 text-[10px] text-slate-400" data-testid={`reminded-at-${it.document_id}`}>
                                                    il {fmtDate(it.ack_reminder_sent_at)}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-2 py-2.5">
                                            <Link to={`/dipendenti/${it.employee_id}`} title="Vai alla scheda dipendente">
                                                <ArrowRight className="h-4 w-4 text-slate-400 hover:text-[#376DB3]" />
                                            </Link>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
